import moment from "moment";
import { compose } from "infrastructure/store";
import { ECommerceOnboardingApplication } from "consts/onboarding";
import { delay } from "util/support";
import { putECommerceBusinessInformationSteps } from "features/onboarding/api";
import type { OnboardingBusinessInformation } from "features/onboarding/types";
import ShoppingPlatforms from "features/onboarding/ShoppingPlatforms";
import { useSimpleRequest } from "hooks";
import { ECommerce } from "ui/forms/Onboarding";
import {
    useFieldsComments,
    useFieldsStatuses,
    useRequestedChanges,
    useApplicationDetailContext,
    useStepStatus,
    useApplicationStatus
} from "ui/organizms/Onboarding/Applications/common";
import { FormAdapter } from "hooks/useForm";

export const useBusinessInformation = () => {
    const { getTotalStepFields, getStepData, fetchStep } = useApplicationDetailContext();

    const totalStepFields = getTotalStepFields(ECommerceOnboardingApplication.BusinessInformation);
    const data = getStepData<OnboardingBusinessInformation>(ECommerceOnboardingApplication.BusinessInformation);

    const {
        getStepStatus
    } = useStepStatus();

    const {
        updateApplicationStatus
    } = useApplicationStatus();

    const {
        withRequestedChangeFields,
        serializeRequestedChangeFields
    } = useRequestedChanges();

    const {
        withFieldsStatuses,
        serializeFieldsStatuses
    } = useFieldsStatuses();

    const {
        withFieldsComments,
        serializeFieldsComments
    } = useFieldsComments();

    const request = useSimpleRequest();

    const stepDataWithRequestedChangeFields = compose<OnboardingBusinessInformation>(
        withRequestedChangeFields,
        withFieldsStatuses,
        withFieldsComments
    )(data);

    const formProps: ECommerce.BusinessInformation.OnboardingBusinessInformationForm = {
        bootstrapData: {},
        initialValues: ECommerce.BusinessInformation.getInitialValues(stepDataWithRequestedChangeFields),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async ({
            companyName,
            registrationNumber,
            yearEstablished,
            websiteUrl,
            primaryLocation,
            countryOfIncorporation = [],
            countriesOfBusinessActivity = [],
            platforms = [],
            ...restFormValues
        }, helpers) => {
            const response = await request(() => putECommerceBusinessInformationSteps({
                userId: data.userId,
                companyName,
                registrationNumber,
                countryOfIncorporation: [countryOfIncorporation]
                    .filter(Boolean)
                    .flat()
                    .join(','),
                yearEstablished: String(moment(yearEstablished).year()),
                websiteUrl,
                primaryLocation,
                countriesOfBusinessActivity: countriesOfBusinessActivity.join(','),
                platforms: ShoppingPlatforms.make()
                    .fromArray(platforms)
                    .serialize(),
                requestedChangeFields: serializeRequestedChangeFields(restFormValues),
                fieldsStatuses: serializeFieldsStatuses(restFormValues),
                fieldsComments: serializeFieldsComments(restFormValues),
                status: getStepStatus(restFormValues, totalStepFields)
            }));

            helpers!.setTouched({});

            await delay(1000)
                .then(() => fetchStep(ECommerceOnboardingApplication.BusinessInformation))
                .then(() => delay(1000))
                .then(updateApplicationStatus);

            return FormAdapter.fromResponse(response);
        }
    };

    return {
        ...formProps,
        ...ECommerce.BusinessInformation.useForm(formProps),
        ...stepDataWithRequestedChangeFields
    };
};

export default useBusinessInformation;
